/**
 * One cache for the whole app.
 *
 * Every screen used to hold its own copy of the same idea: a `useState`, a
 * `useEffect`, a `load()`, the word "Loading" in the middle of the screen, and
 * a `setErr`. Seventy-nine effects and a hundred and eighty lines of loaded /
 * error state, none of it shared — so moving between Home and Tasks fetched
 * everything again from nothing, and going back showed an empty screen a
 * second time. On a phone on a cellular connection that reads as an app that
 * stalls on every touch.
 *
 * The defaults below are chosen for a household app rather than a dashboard:
 * data is worth showing immediately and worth checking quietly, and a failure
 * the server has already explained is not worth retrying.
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Thirty seconds matches what the today screen already polled at, and it
      // is roughly how long a household's answer stays true.
      staleTime: 30_000,
      // Put the phone down, pick it up, and the screen is current. This is the
      // single behaviour that makes a cached app feel live rather than stale.
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      // A 4xx is the server telling us something it will tell us again. Only
      // network and 5xx failures are worth a second attempt.
      retry: (count, error) => {
        if (error instanceof ApiError && error.status < 500) return false;
        return count < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * The two answers that do not change while somebody is looking at them.
 *
 * The household's name, currency and timezone, and who you are — read by at
 * least five screens each and refetched by every one of them on every return
 * to the tab, because the 30-second default above is written for a task queue
 * rather than for a fact. Set here, once, rather than at seven call sites that
 * would drift.
 */
for (const key of ['/v1/household', '/v1/me']) {
  queryClient.setQueryDefaults([key], { staleTime: 15 * 60_000 });
}

/** `useQuery({ queryKey: ['/v1/staff'], queryFn: get })` — the path is the key. */
export function get<T>({ queryKey }: { queryKey: readonly unknown[] }): Promise<T> {
  return api<T>(String(queryKey[0]));
}

/**
 * Everything under this path is now wrong.
 *
 * Approving a task changes the task, the queue it was in, the person's page
 * and the count on the home screen. Rather than name all four at every call
 * site, invalidate the prefix and let the screens that are mounted refetch.
 *
 * Two key conventions live in this app and both work here: a URL key
 * (`['/v1/staff']`, `['/v1/search?q=x']`) is matched a path segment at a time,
 * and a semantic key (`['clarifications']`, `['pilot-case', hh, id]`) is passed
 * as an array and matched element by element, which is react-query's own rule.
 */
export function invalidate(...prefixes: (string | readonly unknown[])[]): void {
  for (const p of prefixes) {
    if (typeof p !== 'string') {
      void queryClient.invalidateQueries({ queryKey: p });
      continue;
    }
    void queryClient.invalidateQueries({
      predicate: (q) => underPath(String(q.queryKey[0] ?? ''), p),
    });
  }
}

/**
 * A path prefix, and only at a boundary.
 *
 * A bare `startsWith` made `/v1/me` a prefix of `/v1/memory` — saving a
 * profile threw away the house rules, and any future endpoint whose name
 * begins with another's would do the same silently.
 */
function underPath(key: string, prefix: string): boolean {
  if (key === prefix) return true;
  if (!key.startsWith(prefix)) return false;
  const next = key[prefix.length];
  return next === '/' || next === '?' || next === '#';
}
